import CableConsumer from 'utils/actionCable';

/**
 * Fallback client for environments without SharedWorker support
 * (e.g. Safari / iOS). Uses the existing per-tab CableConsumer.
 *
 * @class FallbackCableClient
 */
class FallbackCableClient {
    constructor() {
        this.cableConsumer = new CableConsumer();
        // { ticketId → { sub: ActionCable subscription, cbs: callback map } }
        this.subscriptions = {};
    }

    /**
     * Subscribe to presence updates for a ticket via the per-tab consumer.
     *
     * @param {string} ticketId
     * @param {string} token
     * @param {Object} cbs  Callback map: { received, connected, disconnected, rejected }
     */
    subscribe(ticketId, token, cbs) {
        const consumer = this.cableConsumer.getConsumer(token);

        const sub = consumer.subscriptions.create(
            { channel: 'TicketActivityChannel', ticket_id: ticketId },
            {
                received(data) {
                    if (data && data.type === 'activity' && cbs.received) {
                        cbs.received({
                            type: 'viewers_updated',
                            ticketId: data.ticket_id,
                            caseId: data.ticket_id,
                            viewers: data.viewers || [],
                        });
                    }
                },
                connected() {
                    if (cbs.connected) cbs.connected();
                },
                disconnected() {
                    if (cbs.disconnected) cbs.disconnected();
                },
                rejected() {
                    if (cbs.rejected) cbs.rejected();
                },
            }
        );

        this.subscriptions[ticketId] = { sub, cbs };
    }

    /**
     * Unsubscribe from a ticket.
     *
     * @param {string} ticketId
     */
    unsubscribe(ticketId) {
        const entry = this.subscriptions[ticketId];
        if (entry) {
            entry.sub.unsubscribe();
            delete this.subscriptions[ticketId];
        }
    }

    /**
     * Rotate the token on the underlying consumer.
     *
     * @param {string} token
     */
    updateToken(token) {
        const activeTickets = Object.keys(this.subscriptions).map(ticketId => ({
            ticketId,
            cbs: this.subscriptions[ticketId].cbs,
        }));

        activeTickets.forEach(({ ticketId }) => {
            const entry = this.subscriptions[ticketId];
            if (entry) entry.sub.unsubscribe();
            delete this.subscriptions[ticketId];
        });

        this.cableConsumer.getConsumer(token);

        activeTickets.forEach(({ ticketId, cbs }) => {
            this.subscribe(ticketId, token, cbs);
        });
    }

    /**
     * Full disconnect (logout).
     */
    disconnect() {
        Object.keys(this.subscriptions).forEach(ticketId => {
            const entry = this.subscriptions[ticketId];
            if (entry) entry.sub.unsubscribe();
        });
        this.subscriptions = {};
        this.cableConsumer.reset();
    }
}

export default FallbackCableClient;
